import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Button, DatePicker, Flex, Input, Tooltip } from "antd";
import { DownloadOutlined, ReloadOutlined, SearchOutlined, EyeOutlined } from "@ant-design/icons";
import { debounce } from "lodash";
import CustomTable from "../../Component/Table";
import { formatDate } from "../../utils/date";
import { listCustomEmails, exportCustomEmails } from "../../Services/customEmail";
import { CePage } from "./styles";

const { RangePicker } = DatePicker;

const columns = (open) => [
  { title: "#ID", dataIndex: "id", key: "id" },
  {
    title: "Campaign Title",
    dataIndex: "title",
    key: "title",
    render: (text, record) => (
      <Button type="link" style={{ padding: 0 }} onClick={() => open(record.id)}>
        {text && text.length > 40 ? <Tooltip title={text}>{text.slice(0, 40)}...</Tooltip> : text}
      </Button>
    ),
  },
  {
    title: "OneSignal Template",
    dataIndex: "template_name",
    key: "template_name",
    render: (text) => (text && text.length > 30 ? <Tooltip title={text}>{text.slice(0, 30)}...</Tooltip> : text || "-"),
  },
  { title: "Total Recipients", dataIndex: "total_recipients", key: "total_recipients" },
  { title: "Sent", dataIndex: "sent_count", key: "sent_count" },
  { title: "Total Clicks", dataIndex: "total_click_count", key: "total_click_count" },
  { title: "Useful", dataIndex: "useful_count", key: "useful_count" },
  { title: "Not Useful", dataIndex: "not_useful_count", key: "not_useful_count" },
  { title: "Date", dataIndex: "created_at", key: "created_at", render: (text) => (text ? formatDate(text) : "-") },
  {
    title: "Actions",
    key: "actions",
    fixed: "right",
    render: (_, record) => (
      <Tooltip title="View details">
        <Button aria-label={`View campaign ${record.id}`} icon={<EyeOutlined />} type="link" onClick={() => open(record.id)} />
      </Tooltip>
    ),
  },
];

/** Custom Email tab of /email_campaign: campaigns sent from OneSignal templates. */
const CustomEmailList = ({ tabs }) => {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [typed, setTyped] = useState("");
  const [search, setSearch] = useState("");
  const [dateRange, setDateRange] = useState({ start_date: null, end_date: null });

  const applySearch = React.useMemo(() => debounce(setSearch, 500), []);
  useEffect(() => () => applySearch.cancel(), [applySearch]);
  useEffect(() => { setCurrentPage(1); }, [search, dateRange]);

  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ["custom_email_campaigns", currentPage, pageSize, search, dateRange],
    queryFn: () => listCustomEmails({ skip: (currentPage - 1) * pageSize, limit: pageSize, search, ...dateRange }),
    keepPreviousData: true,
    refetchOnWindowFocus: false,
    retry: false,
  });
  const { mutate: triggerExport, isPending: exporting } = useMutation({ mutationFn: () => exportCustomEmails({ search, ...dateRange }) });

  const open = (id) => navigate(`/email_campaign/custom/${id}`);

  return (
    <CePage>
      <div className="head">
        <h2>Email Campaigns</h2>
        {/* same buttons as the Itinerary Email tab */}
        <Flex gap="middle">
          <Button type="primary" onClick={() => navigate("/email_campaign/custom/create")}>Create Custom Email</Button>
          <Button type="primary" icon={<DownloadOutlined />} loading={exporting} onClick={() => triggerExport()}>Export</Button>
          <Button icon={<ReloadOutlined />} loading={isFetching} onClick={() => refetch()}>Refresh</Button>
        </Flex>
      </div>
      {tabs}
      <div className="filters">
        <Input aria-label="Search by campaign title" prefix={<SearchOutlined style={{ color: "#8a90a8" }} />} allowClear placeholder="Search by campaign title"
          style={{ flex: "1 1 220px" }} value={typed} onChange={(e) => { setTyped(e.target.value); applySearch(e.target.value.trim()); }} />
        <RangePicker
          onChange={(d) => setDateRange(d ? { start_date: d[0].format("YYYY-MM-DD"), end_date: d[1].format("YYYY-MM-DD") } : { start_date: null, end_date: null })}
          disabledDate={(cur) => cur && cur.toDate() > new Date()} format="DD MMM YYYY"
        />
      </div>
      <CustomTable
        columns={columns(open)}
        data={data?.campaigns || []}
        loading={isLoading}
        pagination={{
          current: currentPage,
          pageSize,
          total: data?.total_count || 0,
          showSizeChanger: true,
          pageSizeOptions: ["10", "20", "50", "100"],
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
          position: ["bottomRight"],
        }}
        onTableChange={(p) => { setCurrentPage(p.current); setPageSize(p.pageSize); }}
        maxHeight="calc(100vh - 300px)"
      />
      <p className="note" style={{ marginTop: 10 }}>
        Export uses the same filters and has three sheets: <b>Campaigns</b>, <b>Recipient Link Clicks</b> (one row per user per link, with the click count) and <b>Click Log</b> (one row per click). Test sends never appear.
      </p>
    </CePage>
  );
};

export default CustomEmailList;
